import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { ChatBox } from './ChatBox';
import { useConversation, useConversations } from './useConversation';
import { avatarColor, initials, isActiveNow } from './chatVisuals';

/**
 * Full-page chat view at /messages/:conversationId. Sticky header with a back
 * button + the other participant's avatar/name; the rest of the viewport is
 * the message list and input. Replaces the embedded split-pane chat — works
 * the same way WhatsApp Web and Instagram DMs do once a thread is open.
 */
export function ConversationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { detail } = useConversation(conversationId ?? null);
  // Fall back to the inbox summary while detail loads — avoids a blank header
  // flash when navigating from /messages.
  const { conversations } = useConversations();
  const summary = conversations.find((c) => c.id === conversationId);

  const other = (() => {
    if (!detail) return null;
    // The "other" side from the current user's POV. The detail endpoint exposes
    // both sides; we use the one that matches the inbox summary, or just pick
    // the provider as a sensible default when both sides resolve.
    if (summary) {
      if (summary.otherParticipant.userId === detail.customer.userId) return detail.customer;
      if (summary.otherParticipant.userId === detail.provider.userId) return detail.provider;
    }
    return detail.provider;
  })();

  const label =
    other?.businessName ??
    other?.name ??
    summary?.otherParticipant.businessName ??
    summary?.otherParticipant.name ??
    '';
  const seedId = other?.userId ?? summary?.otherParticipant.userId ?? conversationId ?? '';
  const isOnline = isActiveNow(summary?.lastMessageAt ?? detail?.lastMessageAt ?? null);

  useEffect(() => {
    if (!conversationId) navigate('/messages', { replace: true });
  }, [conversationId, navigate]);

  if (!conversationId) return null;

  return (
    <Box
      sx={{
        // `position: fixed` anchors to the visible viewport — bypasses the
        // AppShell's padding and dodges the mobile-browser `100vh` quirk
        // (URL bar inflates vh, pushing the input off-screen). top/left
        // mirror the AppBar height and the permanent drawer width on
        // desktop so we sit precisely under/beside them without overlap.
        position: 'fixed',
        top: { xs: 56, sm: 64 },
        left: { xs: 0, md: '248px' },
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        zIndex: 1,
      }}
    >
      {/* Sticky chat header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flex: '0 0 auto',
          bgcolor: 'background.paper',
        }}
      >
        <IconButton
          onClick={() => navigate('/messages')}
          aria-label={t('common.back', 'Retour')}
          edge="start"
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box
          sx={{
            position: 'relative',
            flex: '0 0 auto',
            ...(isOnline && {
              p: '2px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #25F4EE 0%, #FE2C55 100%)',
            }),
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: avatarColor(seedId),
              color: 'common.white',
              fontSize: 15,
              fontWeight: 600,
              border: isOnline ? '2px solid' : 'none',
              borderColor: 'background.paper',
            }}
          >
            {initials(label || '?')}
          </Avatar>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label || t('chat.title')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isOnline ? 'success.main' : 'text.secondary' }}
          >
            {isOnline ? t('chat.activeNow', 'En ligne') : t('chat.offline', 'Hors ligne')}
          </Typography>
        </Box>
      </Stack>

      {/* Chat body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ChatBox conversationId={conversationId} />
      </Box>
    </Box>
  );
}
